import BaseStore from 'bz-stores/mobx/base-store';

import { createContextForClass } from 'bz-utilities/hooks/context-v2';
import CacheStore from 'bz-utilities/modules/stores/cached-map-store';

import { action } from 'mobx';
import { createTransformer } from 'mobx-utils';

import type {
  AddLookupItem,
  DeleteLookupItem,
  LookupResponse,
  RegionalLookup
} from 'tm3-core/api.dtos';
import { RegionalLookupRequest } from 'tm3-core/api.dtos';

interface IUpdateListLookups {
  isAdd?: boolean;
  valueUpdate?: AddLookupItem | DeleteLookupItem;
}

export enum AutocompleteValues {
  None,
  City,
  Region,
  Country
}

class LookupStore extends CacheStore<LookupResponse> {
  constructor() {
    super('lookups');
  }

  private regionalLookups: RegionalLookup[] = [];

  @action.bound
  async LoadRegionalLookups() {
    this.regionalLookups = (await BaseStore.defaultClient.get(new RegionalLookupRequest())).results;
  }

  readonly GetRegionalLookups = async (
    lookup: AutocompleteValues,
    city: string = '',
    region: string = '',
    country: string = ''
  ) => {
    if (this.regionalLookups.length === 0) await this.LoadRegionalLookups();
    return this.regionalLookups
      .filter((x) => {
        if (!!city && x.city !== city) return false;
        if (!!region && x.region !== region) return false;
        if (!!country && x.country !== country) return false;
        return true;
      })
      .map((x) => {
        switch (lookup) {
          case AutocompleteValues.City:
            return x.city;
          case AutocompleteValues.Country:
            return x.country;
          case AutocompleteValues.Region:
            return x.region;
          default:
            return '';
        }
      });
  };

  readonly GetLookups = createTransformer((lookupName: string) => {
    return this.All.find((x) => x.name === lookupName);
  });

  @action.bound
  UpdateListLookups({ valueUpdate, isAdd }: IUpdateListLookups) {
    if (!valueUpdate || !valueUpdate.hasOwnProperty('name')) {
      return;
    }

    const res = this.GetLookups(valueUpdate.name);
    if (!res) {
      return;
    }

    if (res.hasOwnProperty('values') && valueUpdate.hasOwnProperty('value') && isAdd) {
      res.values.push(valueUpdate.value);
    } else {
      res.values = res.values.filter((val) => val !== valueUpdate.value);
    }
    this.atom.reportChanged();
  }
}

export const [LookupStoreCtx, useLookupStore] = createContextForClass(LookupStore);

export default LookupStore;
